import React, { useState, useEffect, useCallback, useRef } from "react";
import "../../assets/css/KnowledgeBase.css";
import "../../assets/css/Urls.css";
import { ChevronLeft, ChevronRight, Hash, Clock, ExternalLink, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { getProjectPid } from "../../utils/sessionProject";

const getAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const viewOriginalDocument = async (mediaPid) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/api/v1/uploads/media/${mediaPid}`,
      { headers: getAuthHeader() }
    );
    if (!response.ok) {
      throw new Error(`Failed to load document (${response.status})`);
    }
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    window.open(objectUrl, "_blank");
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  } catch (err) {
    console.error("Failed to open original document", err);
    toast.error(err?.message || "Failed to load document");
  }
};

const getPaginationItems = (currentPage, totalPages) => {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
  if (currentPage <= 4) return [1, 2, 3, 4, 5, "end-ellipsis", totalPages];
  if (currentPage >= totalPages - 3)
    return [1, "start-ellipsis", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  return [1, "start-ellipsis", currentPage - 1, currentPage, currentPage + 1, "end-ellipsis", totalPages];
};

const KnowledgeBase = () => {
  const itemsPerPage = 10;
  const navigate = useNavigate();
  const projectPid = getProjectPid();

  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const searchRef = useRef("");
  const searchDebounceRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [documents, setDocuments] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchDocuments = useCallback(async (page, search, type, status) => {
    setLoading(true);
    try {
      const body = { projectPid, page, limit: itemsPerPage };
      if (search) body.search = search;
      if (type !== "all") body.type = type;
      if (status !== "all") body.status = status;
      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/v1/knowledge-base/list`,
        body,
        { headers: getAuthHeader() }
      );
      const items = res.data?.response?.items ?? [];
      const total = res.data?.response?.pagination?.total ?? items.length;
      setDocuments(items);
      setTotalCount(total);
    } catch (err) {
      console.error('Failed to fetch documents', err);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [projectPid]);

  // Page change — read latest search from ref
  useEffect(() => {
    fetchDocuments(currentPage, searchRef.current, typeFilter, statusFilter);
  }, [currentPage, fetchDocuments]);

  // Filter change — immediate, reset page
  useEffect(() => {
    setCurrentPage(1);
    fetchDocuments(1, searchRef.current, typeFilter, statusFilter);
  }, [typeFilter, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));
  const pageStartIndex = (currentPage - 1) * itemsPerPage;
  const paginationItems = getPaginationItems(currentPage, totalPages);

  return (
    <main className="app-wrapper url-list-page">
      <div className="container-fluid">

        {/* Toolbar */}
        <section className="url-list-toolbar p-3 mb-3 d-flex align-items-center justify-content-between gap-3 flex-wrap">
          <div className="url-filter-group d-flex align-items-center gap-2 flex-grow-1">
            <div className="url-search-field">
              <i className="fa-solid fa-magnifying-glass"></i>
              <input
                type="search"
                className="form-control"
                placeholder="Search documents by title, keywords..."
                value={searchTerm}
                onChange={(e) => {
                  const val = e.target.value;
                  searchRef.current = val;
                  setSearchTerm(val);
                  clearTimeout(searchDebounceRef.current);
                  searchDebounceRef.current = setTimeout(() => {
                    setCurrentPage(1);
                    fetchDocuments(1, val, typeFilter, statusFilter);
                  }, 400);
                }}
              />
            </div>
          </div>
          <button
            className="btn btn-primary d-flex align-items-center gap-2"
            style={{ fontSize: 13, borderRadius: 8, whiteSpace: "nowrap" }}
            onClick={() => navigate("/knowledge-base-create")}
          >
            <Plus size={15} /> Create Document
          </button>
        </section>

        {/* Table */}
        <section className="url-table-card">
          <div className="table-responsive">
            <table className="table url-table kb-table table-hover align-middle mb-0">
              <colgroup>
                <col style={{ width: 54 }} />
                <col style={{ width: "32%" }} />
                <col style={{ width: "27%" }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 100 }} />
                <col style={{ width: 140 }} />
                <col style={{ width: 90 }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="text-center">#</th>
                  <th>Document Title</th>
                  <th>Source URL</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Chunks</th>
                  <th>Last Updated</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center text-muted py-5">Loading...</td>
                  </tr>
                ) : documents.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center text-muted py-5">No documents found.</td>
                  </tr>
                ) : (
                  documents.map((item, index) => (
                    <tr
                      className="history-row"
                      key={item.doc_pid}
                      onClick={() => navigate(`/knowledge-base-details/${item.doc_pid}`)}
                    >
                      <td className="url-index text-center">{pageStartIndex + index + 1}</td>
                      <td>
                        <p className="kb-doc-title">{item.title}</p>
                      </td>
                      <td>
                        {item.isUploadedDoc ? (
                          <button
                            type="button"
                            className="kb-source-url kb-source-upload-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              viewOriginalDocument(item.source_url);
                            }}
                          >
                            <ExternalLink size={11} />
                            <span>View original document</span>
                          </button>
                        ) : item.source_url ? (
                          <a
                            href={item.source_url}
                            target="_blank"
                            rel="noreferrer"
                            className="kb-source-url"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink size={11} />
                            <span>{item.source_url.replace("https://", "")}</span>
                          </a>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="text-center">
                        <span className={`kb-status-badge status-${item.status || "draft"}`}>
                          {item.status || "draft"}
                        </span>
                      </td>
                      <td className="text-center">
                        {item.chunks ?? 0}
                      </td>
                      <td>
                        <span className="kb-date">
                          <Clock size={11} />
                          {item.created_at ? new Date(item.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                        </span>
                      </td>
                      <td>
                        <button className="ph-view-btn">
                          View <ChevronRight size={13} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalCount > 0 && (
            <div className="url-pagination-wrap">
              <p className="url-pagination-summary mb-0">
                Showing {pageStartIndex + 1}–{Math.min(pageStartIndex + itemsPerPage, totalCount)} of {totalCount}
              </p>
              <nav>
                <ul className="url-pagination mb-0">
                  <li>
                    <button className="url-page-btn" disabled={currentPage === 1}
                      onClick={() => setCurrentPage((p) => p - 1)}>
                      <ChevronLeft size={16} />
                    </button>
                  </li>
                  {paginationItems.map((item) => (
                    <li key={item}>
                      {typeof item === "string" ? (
                        <span className="url-page-ellipsis">...</span>
                      ) : (
                        <button
                          className={`url-page-btn${item === currentPage ? " active" : ""}`}
                          onClick={() => setCurrentPage(item)}
                        >
                          {item}
                        </button>
                      )}
                    </li>
                  ))}
                  <li>
                    <button className="url-page-btn" disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage((p) => p + 1)}>
                      <ChevronRight size={16} />
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </section>
      </div>

    </main>
  );
};

export default KnowledgeBase;
